import { screenshotCdnUrl } from '@symbo.ls/channels'

// FA204 / frank trap — every export below used to reach across to a sibling
// export bare (ownerMatches -> ownerSlug, sanitizeStoredThumbnail ->
// isLegacyScreenshotUrl, projectScreenshotUrl -> SCREENSHOT_CDN_READY/
// ownerSlug/projectKey, groupByOwner -> ownerSlug) or a module-local
// (_isObjectId/_SKIP_CDN_OWNERS). Unresolvable once each export is
// individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers). Each export now carries its
// own self-contained inline copy; the `@symbo.ls/channels` import stays
// untouched (cross-file imports are not part of this trap).

// Canonical owner slug for a project — org-owned projects carry
// `{ type: 'organization', slug }`, user-owned carry `{ username }`.
export const ownerSlug = (project) => {
  const o = project?.owner
  if (!o) return project?.ownerUsername || ''
  if (typeof o === 'string') return o
  return o.slug || o.username || ''
}

export const ownerMatches = (project, slug) => {
  // FA204 / frank trap — self-contained inline copy of ownerSlug, see the
  // file header.
  const _ownerSlug = (project) => {
    const o = project?.owner
    if (!o) return project?.ownerUsername || ''
    if (typeof o === 'string') return o
    return o.slug || o.username || ''
  }
  return _ownerSlug(project) === slug
}

// Strip the legacy `.symbo.ls` / `.tld` suffix from a project key.
export const projectKey = (project) => (project?.key || '').split('.')[0]

// Screenshot CDN — see architecture/SCREENSHOTS.md. URL composition lives
// in `@symbo.ls/channels` (`screenshotCdnUrl`) so server, frontend, CLI,
// and workers all derive from the same host (`screenshots.api.symbols.app`
// post-migration). `page` defaults to 'root' (the `/` route). Pass any
// page slug (`pricing`, `about`, …) to address a per-page capture.
export const SCREENSHOT_CDN_READY = true

// Legacy thumbnails: previously hosted on `screenshots.symbo.ls`. Two
// distinct dead shapes now surface as a `Project.thumbnail` value the
// canvas tile must drop so the canonical cascade can run:
//   1. Flat filenames (screenshotapi.net mirror era):
//        https://screenshots.symbo.ls/{flat_filename}.webp
//   2. The entire old host — `screenshots.symbo.ls` is being retired
//        in favor of `screenshots.api.symbols.app`. Any thumbnail still
//        pointing at the old apex is considered legacy.
export const isLegacyScreenshotUrl = (url) => {
  if (typeof url !== 'string' || !url) return false
  return /^https?:\/\/screenshots\.symbo\.ls\//i.test(url)
}

export const sanitizeStoredThumbnail = (url) => {
  // FA204 / frank trap — self-contained inline copy of isLegacyScreenshotUrl,
  // see the file header.
  const _isLegacyScreenshotUrl = (url) => {
    if (typeof url !== 'string' || !url) return false
    return /^https?:\/\/screenshots\.symbo\.ls\//i.test(url)
  }
  return _isLegacyScreenshotUrl(url) ? null : url || null
}

export const projectScreenshotUrl = (project, { page = 'root', env = 'production' } = {}) => {
  // FA204 / frank trap — self-contained inline copies, see the file header.
  const SCREENSHOT_CDN_READY = true
  const _ownerSlug = (project) => {
    const o = project?.owner
    if (!o) return project?.ownerUsername || ''
    if (typeof o === 'string') return o
    return o.slug || o.username || ''
  }
  const _projectKey = (project) => (project?.key || '').split('.')[0]
  // Mongo ObjectId pattern — 24 hex chars. Projects whose owner or key is
  // a raw ObjectId have not been migrated to human-readable slugs yet and
  // will not have a CDN screenshot. Returning null lets the tile fall back
  // to the letter-placeholder gradient, eliminating the 404 noise (#2185).
  const _isObjectId = (s) => /^[a-f0-9]{24}$/i.test(String(s || ''))
  // Owner slugs to skip the CDN fetch for (framework/demo entries with no
  // captured screenshots — avoids 404 noise, #2314). Emptied 2026-06-09 per
  // Nika: the `system` org's projects now have captured screenshots on the
  // CDN (verified system/landing/root.webp → 200), so they load when
  // available and only fall back to the gradient for a project that
  // genuinely has none.
  const _SKIP_CDN_OWNERS = new Set()

  if (!SCREENSHOT_CDN_READY) return null
  const owner = _ownerSlug(project)
  const key = _projectKey(project)
  if (_isObjectId(owner) || _isObjectId(key)) return null
  if (_SKIP_CDN_OWNERS.has(String(owner || '').toLowerCase())) return null
  return screenshotCdnUrl({ owner, key, page, env })
}

// Group projects by workspace (owner slug). Returns an array of
// `{ owner, items }` pairs preserving insertion order.
export const groupByOwner = (projects) => {
  // FA204 / frank trap — self-contained inline copy of ownerSlug, see the
  // file header.
  const _ownerSlug = (project) => {
    const o = project?.owner
    if (!o) return project?.ownerUsername || ''
    if (typeof o === 'string') return o
    return o.slug || o.username || ''
  }
  const map = new Map()
  for (const p of projects) {
    const slug = _ownerSlug(p) || 'unknown'
    if (!map.has(slug)) map.set(slug, [])
    map.get(slug).push(p)
  }
  return [...map.entries()].map(([owner, items]) => ({ owner, items }))
}
